import { useState, useEffect, useRef, useCallback } from 'react';

interface UseIdleTimerProps {
  onIdle: () => void;
  idleTime: number; // in seconds
  promptTime: number; // in seconds before idle
  enabled?: boolean;
}

export const useIdleTimer = ({ onIdle, idleTime, promptTime, enabled = true }: UseIdleTimerProps) => {
  const [isPromptActive, setIsPromptActive] = useState(false);
  const lastActivity = useRef(Date.now());
  const animationFrame = useRef<number>();
  const onIdleRef = useRef(onIdle);
  const isPromptActiveRef = useRef(isPromptActive);

  // Keep onIdle and isPromptActive refs updated
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    isPromptActiveRef.current = isPromptActive;
  }, [isPromptActive]);

  // Does nothing if the prompt is active.
  const handleActivity = useCallback(() => {
    if (isPromptActiveRef.current) {
      return;
    }
    lastActivity.current = Date.now();
  }, []);

  // Explicitly dismisses the prompt and resets the timer.
  const stay = () => {
    lastActivity.current = Date.now();
    setIsPromptActive(false);
  };

  useEffect(() => {
    if (!enabled) {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      return;
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(event => window.addEventListener(event, handleActivity));
    
    // Reset timer on mount or when re-enabled
    lastActivity.current = Date.now();
    setIsPromptActive(false);

    const checkIdle = () => {
      const now = Date.now();
      const elapsed = (now - lastActivity.current) / 1000;

      // Show the prompt if the user has been idle long enough
      if (!isPromptActiveRef.current && elapsed >= idleTime - promptTime) {
        setIsPromptActive(true);
      }

      // If the total idle time is exceeded, call the onIdle callback
      if (elapsed >= idleTime) {
        onIdleRef.current();
        stay(); // Reset after the idle action is performed
      } else {
        animationFrame.current = requestAnimationFrame(checkIdle);
      }
    };

    animationFrame.current = requestAnimationFrame(checkIdle);

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [enabled, idleTime, promptTime, handleActivity]);

  return { isPromptActive, stay };
};
